import React from 'react';

import { getSessionTimer } from '../helpers/sessionTimer';
import { throttle } from '../utils/throttle';

export const useSessionTimer = (initRef: React.RefObject<boolean>, hasSession: boolean, isPlaywright: boolean) => {
  const sessionRef = React.useRef<ReturnType<typeof getSessionTimer>>(undefined);

  React.useEffect(() => {
    if (isPlaywright) {
      return;
    }
    const ping = throttle((activityType: 'scroll' | 'mouse-movement' | 'keyboard' | 'iframe-interaction') => {
      sessionRef.current?.userActivity(activityType);
    }, 1000);

    const cleanup = () => {
      window.removeEventListener('keydown', () => ping('keyboard'));
      window.removeEventListener('pointermove', () => ping('mouse-movement'));
      window.removeEventListener('scroll', () => ping('scroll'));
      window.removeEventListener('iframe-interaction', () => ping('iframe-interaction'));
      sessionRef.current?.destroy('Session timer hook is unmounting.');
      initRef.current = false;
    };

    // we haven't initialised the timer yet and we are logged in.
    if (!initRef.current && hasSession) {
      sessionRef.current = getSessionTimer({
        checkIntervalSeconds: 10,
        sessionDurationSeconds: Number(process.env.NEXT_PUBLIC_SESSION_DURATION_SECONDS ?? 590), // shave off 10 seconds to account for network latency.
      });

      window.addEventListener('keydown', () => ping('keyboard'));
      window.addEventListener('pointermove', () => ping('mouse-movement'));
      window.addEventListener('scroll', () => ping('scroll'));
      window.addEventListener('iframe-interaction', () => ping('iframe-interaction'));

      initRef.current = true;
    }

    // we have initialised the timer previously, but we are now logged out, so we need to destroy the timer.
    if (initRef.current && !hasSession) {
      cleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initRef, hasSession]);

  return null;
};
